/**
 * Spaces Settings
 *
 * Manage collaboration spaces within the current workspace.
 * Allows creating personal, team, and org spaces; managing members; and
 * sending invitations.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Users,
  Building2,
  Plus,
  Trash2,
  UserPlus,
  Link2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  X,
  Shield,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useToast } from '../../../components/ui/Toast';
import { useSpaceStore } from '../../../stores/prioritization/spaceStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import {
  getSpaceMembers,
  getSpaceInvitations,
  createSpaceInvitation,
  updateSpaceMemberRole,
  removeSpaceMember,
  createSpace,
} from '../../../lib/spaceService';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { ReverseActionButton } from '../../../components/reversals/ReverseActionButton';
import { revokeSpaceInvitation } from '../../../lib/reversals/reversalsService';
import type {
  SpaceMembership,
  SpaceInvitation,
  SpaceMembershipRole,
  SpaceType,
} from '../../../types/prioritization/collaborationSpaces';
import {
  SPACE_TYPE_LABELS,
  SPACE_ROLE_LABELS,
} from '../../../types/prioritization/collaborationSpaces';
import { CopyButton } from '../../../components/ds';

// ── Icons ─────────────────────────────────────────────────────────────────────

function SpaceTypeIcon({ type, className }: { type: SpaceType; className?: string }) {
  const cls = className ?? 'h-5 w-5';
  if (type === 'personal') return <User className={cls} />;
  if (type === 'team') return <Users className={cls} />;
  return <Building2 className={cls} />;
}

const TYPE_BG: Record<SpaceType, string> = {
  personal: 'bg-[var(--slate-dim)]',
  team:     'bg-[var(--slate-dim)]',
  org:      'bg-primary-100',
};

const TYPE_ICON_COLOR: Record<SpaceType, string> = {
  personal: 'text-[var(--text-ter)]',
  team:     'text-[var(--text-ter)]',
  org:      'text-primary-600',
};

const TYPE_BADGE: Record<SpaceType, string> = {
  personal: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]',
  team:     'bg-[var(--slate-dim)] text-[var(--text-sec)] border border-[var(--slate)]',
  org:      'bg-primary-50 text-primary-700 border border-primary-200',
};

const ROLE_ORDER: SpaceMembershipRole[] = ['owner', 'admin', 'member', 'viewer', 'guest'];

// ── Main page ─────────────────────────────────────────────────────────────────

export function SpacesSettings() {
  const { mySpaces, isLoading, fetchSpaces, bootstrapSpaces, deleteSpace: storeDeleteSpace } = useSpaceStore();
  const currentWorkspaceId = useTenantStore(s => s.currentWorkspaceId);
  const userId = useUserStore(s => s.userId);
  const canManageOrg = useUserStore(s => s.canAny(['admin.users', 'admin.settings']));
  const { showToast } = useToast();

  const [expandedSpaceId, setExpandedSpaceId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (currentWorkspaceId) {
      bootstrapSpaces(currentWorkspaceId);
    }
  }, [currentWorkspaceId, bootstrapSpaces]);

  async function handleDelete(spaceId: string, spaceName: string) {
    if (!confirm(`Delete "${spaceName}"? This cannot be undone.`)) return;
    const ok = await storeDeleteSpace(spaceId);
    if (ok) {
      showToast('success', `"${spaceName}" deleted`, undefined, 3000);
    } else {
      showToast('error', 'Could not delete space', 'You may not have permission to delete this space.', 5000);
    }
  }

  const personal = mySpaces.filter(s => s.spaceType === 'personal');
  const team = mySpaces.filter(s => s.spaceType === 'team');
  const org = mySpaces.filter(s => s.spaceType === 'org');

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Shield className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-100"
        title="Spaces"
        subtitle="Manage your personal, team, and org-wide collaboration spaces"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            New Space
          </button>
        }
      />

      {isLoading && mySpaces.length === 0 ? (
        <div className="flex items-center gap-2 text-[var(--text-ter)] text-sm">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Loading spaces…
        </div>
      ) : (
        <div className="space-y-8">
          <SpaceGroup
            label="Personal"
            description="Your private workspace. Invite others as guests."
            spaces={personal}
            currentUserId={userId}
            expandedSpaceId={expandedSpaceId}
            onToggleExpand={id => setExpandedSpaceId(id === expandedSpaceId ? null : id)}
            onDelete={handleDelete}
          />
          <SpaceGroup
            label="Team"
            description="Shared spaces for your teams."
            spaces={team}
            currentUserId={userId}
            expandedSpaceId={expandedSpaceId}
            onToggleExpand={id => setExpandedSpaceId(id === expandedSpaceId ? null : id)}
            onDelete={handleDelete}
          />
          <SpaceGroup
            label="Organization"
            description="The org-wide space — visible to all workspace members."
            spaces={org}
            currentUserId={userId}
            expandedSpaceId={expandedSpaceId}
            onToggleExpand={id => setExpandedSpaceId(id === expandedSpaceId ? null : id)}
            onDelete={handleDelete}
          />
        </div>
      )}

      {showCreateModal && currentWorkspaceId && (
        <CreateSpaceModal
          workspaceId={currentWorkspaceId}
          canCreateOrgSpace={canManageOrg}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            if (currentWorkspaceId) fetchSpaces(currentWorkspaceId);
          }}
        />
      )}
    </div>
  );
}

// ── Space group ───────────────────────────────────────────────────────────────

function SpaceGroup({
  label,
  description,
  spaces,
  currentUserId,
  expandedSpaceId,
  onToggleExpand,
  onDelete,
}: {
  label: string;
  description: string;
  spaces: import('../../../types/prioritization/collaborationSpaces').MySpace[];
  currentUserId: string;
  expandedSpaceId: string | null;
  onToggleExpand: (id: string) => void;
  onDelete: (id: string, name: string) => void;
}) {
  if (spaces.length === 0) return null;
  return (
    <div>
      <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-1">{label} Spaces</h3>
      <p className="text-xs text-[var(--text-ter)] mb-3">{description}</p>
      <div className="space-y-3">
        {spaces.map(space => (
          <SpaceCard
            key={space.id}
            space={space}
            currentUserId={currentUserId}
            isExpanded={expandedSpaceId === space.id}
            onToggleExpand={() => onToggleExpand(space.id)}
            onDelete={() => onDelete(space.id, space.name)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Space card ─────────────────────────────────────────────────────────────────

function SpaceCard({
  space,
  currentUserId,
  isExpanded,
  onToggleExpand,
  onDelete,
}: {
  space: import('../../../types/prioritization/collaborationSpaces').MySpace;
  currentUserId: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
}) {
  const isOwnerOrAdmin = space.memberRole === 'owner' || space.memberRole === 'admin';

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className={`w-9 h-9  flex items-center justify-center flex-shrink-0 ${TYPE_BG[space.spaceType]}`}>
          <SpaceTypeIcon type={space.spaceType} className={`h-5 w-5 ${TYPE_ICON_COLOR[space.spaceType]}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[var(--text-pri)] truncate">{space.name}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_BADGE[space.spaceType]}`}>
              {SPACE_TYPE_LABELS[space.spaceType]}
            </span>
          </div>
          <div className="text-xs text-[var(--text-ter)] mt-0.5">
            Your role: <span className="font-medium text-[var(--text-sec)]">{SPACE_ROLE_LABELS[space.memberRole]}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isOwnerOrAdmin && space.spaceType === 'personal' && (
            <button
              onClick={onDelete}
              className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
              title="Delete space"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onToggleExpand}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <SpaceDetail
          space={space}
          currentUserId={currentUserId}
          canManage={isOwnerOrAdmin}
        />
      )}
    </div>
  );
}

// ── Space detail (members + invitations) ──────────────────────────────────────

function SpaceDetail({
  space,
  currentUserId,
  canManage,
}: {
  space: import('../../../types/prioritization/collaborationSpaces').MySpace;
  currentUserId: string;
  canManage: boolean;
}) {
  const [members, setMembers] = useState<SpaceMembership[]>([]);
  const [invitations, setInvitations] = useState<SpaceInvitation[]>([]);
  const [users, setUsers] = useState<Record<string, { name: string; email: string }>>({});
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [mems, invs] = await Promise.all([
      getSpaceMembers(space.id),
      canManage ? getSpaceInvitations(space.id) : Promise.resolve([]),
    ]);
    setMembers(mems);
    setInvitations(invs);

    // Fetch user display info
    if (mems.length > 0) {
      const supabase = getSupabaseClient();
      const { data } = await supabase
        .from('users')
        .select('id, name, email')
        .in('id', mems.map(m => m.userId));
      const map: Record<string, { name: string; email: string }> = {};
      for (const u of (data ?? [])) {
        map[u.id] = { name: u.name, email: u.email };
      }
      setUsers(map);
    }

    setLoading(false);
  }, [space.id, canManage]);

  useEffect(() => { load(); }, [load]);

  const [mutationError, setMutationError] = useState<string | null>(null);

  async function handleRoleChange(userId: string, role: SpaceMembershipRole) {
    setMutationError(null);
    const result = await updateSpaceMemberRole(space.id, userId, role);
    if (!result.success) {
      setMutationError(result.blocked?.message ?? result.error ?? 'Failed to change role');
    }
    load();
  }

  async function handleRemove(userId: string) {
    if (!confirm('Remove this member from the space?')) return;
    setMutationError(null);
    const result = await removeSpaceMember(space.id, userId);
    if (!result.success) {
      setMutationError(result.blocked?.message ?? result.error ?? 'Failed to remove member');
    }
    load();
  }

  return (
    <div className="border-t border-[var(--rule)] px-5 py-4 bg-[var(--surface)]">
      {loading ? (
        <div className="flex items-center gap-2 text-[var(--text-ter)] text-sm">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="space-y-4">
          {mutationError && (
            <div className="text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] rounded px-2 py-1.5">
              {mutationError}
            </div>
          )}

          {/* Members */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
                Members ({members.length})
              </h4>
              {canManage && (
                <button
                  onClick={() => setShowInviteForm(v => !v)}
                  className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Invite
                </button>
              )}
            </div>

            {showInviteForm && (
              <InviteForm
                spaceId={space.id}
                onDone={() => { setShowInviteForm(false); load(); }}
                onCancel={() => setShowInviteForm(false)}
              />
            )}

            <div className="space-y-1">
              {members.map(m => {
                const u = users[m.userId];
                const isSelf = m.userId === currentUserId;
                return (
                  <div key={m.id} className="flex items-center gap-2 py-1.5">
                    <div className="w-7 h-7 rounded-full bg-[var(--hover-tint)] flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-[var(--text-sec)]">
                        {u?.name?.[0]?.toUpperCase() ?? '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[var(--text-pri)] truncate">
                        {u?.name ?? m.userId}
                        {isSelf && <span className="ml-1 text-xs text-[var(--text-ter)]">(you)</span>}
                      </div>
                      <div className="text-xs text-[var(--text-ter)] truncate">{u?.email}</div>
                    </div>
                    {canManage && !isSelf ? (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <select
                          value={m.role}
                          onChange={e => handleRoleChange(m.userId, e.target.value as SpaceMembershipRole)}
                          className="text-xs border border-[var(--rule)] rounded px-1.5 py-1 text-[var(--text-sec)] bg-[var(--bg-raised)] focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-100"
                        >
                          {ROLE_ORDER.filter(r => r !== 'owner').map(r => (
                            <option key={r} value={r}>{SPACE_ROLE_LABELS[r]}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleRemove(m.userId)}
                          className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-[var(--text-ter)] flex-shrink-0">
                        {SPACE_ROLE_LABELS[m.role]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pending invitations */}
          {canManage && invitations.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
                Pending Invitations
              </h4>
              <div className="space-y-1">
                {invitations.map(inv => (
                  <div key={inv.id} className="flex items-center gap-2 py-1.5">
                    <Link2 className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-[var(--text-sec)] truncate">
                        {inv.email ?? 'Anyone with the link'}
                      </div>
                      <div className="text-xs text-[var(--text-ter)]">
                        Role: {SPACE_ROLE_LABELS[inv.role]} · Expires {inv.expiresAt.toLocaleDateString()}
                      </div>
                    </div>
                    <InviteLinkCopy token={inv.token} />
                    <ReverseActionButton
                      label="Revoke invitation"
                      confirmTitle="Revoke invitation"
                      explain={`This will expire the invitation for ${inv.email ?? 'anyone with the link'}. Anyone who has not yet used it will no longer be able to join.`}
                      run={(reason) => revokeSpaceInvitation(inv.id, reason)}
                      onDone={load}
                      variant="ghost"
                      size="sm"
                      className="!px-2 !py-1 !text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Policies */}
          {canManage && <SpacePoliciesSection spaceId={space.id} />}
        </div>
      )}
    </div>
  );
}

// ── Invite form ────────────────────────────────────────────────────────────────

function InviteForm({
  spaceId,
  onDone,
  onCancel,
}: {
  spaceId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Exclude<SpaceMembershipRole, 'owner'>>('member');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ token: string } | null>(null);

  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const res = await createSpaceInvitation({
      spaceId,
      email: email || undefined,
      role,
      expiresInDays: 7,
    });
    setSubmitting(false);
    if (res.invitation) {
      setResult({ token: res.invitation.token });
    } else if (res.blocked) {
      setError(res.blocked.message);
    } else {
      setError(res.error ?? 'Failed to create invitation');
    }
  }

  if (result) {
    const link = `${window.location.origin}/invite/space/${result.token}`;
    return (
      <div className="mb-3 p-3 bg-[var(--green-dim)] border border-[var(--green)]">
        <div className="text-xs font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-1">Invitation created</div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] truncate flex-1">{link}</span>
          <CopyButton text={link} />
        </div>
        <button onClick={onDone} className="mt-2 text-xs text-[var(--green)] dark:text-[var(--text-ter)] underline">Done</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mb-3 p-3 bg-[var(--bg-raised)] border border-[var(--rule)] space-y-2">
      <div className="flex gap-2">
        <input
          type="email"
          placeholder="Email (optional)"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="flex-1 text-xs border border-[var(--rule)] rounded px-2 py-1.5 focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-100"
        />
        <select
          value={role}
          onChange={e => setRole(e.target.value as Exclude<SpaceMembershipRole, 'owner'>)}
          className="text-xs border border-[var(--rule)] rounded px-2 py-1.5 text-[var(--text-sec)] bg-[var(--bg-raised)] focus:outline-none focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-100"
        >
          {(['admin', 'member', 'viewer', 'guest'] as const).map(r => (
            <option key={r} value={r}>{SPACE_ROLE_LABELS[r]}</option>
          ))}
        </select>
      </div>
      {error && (
        <div className="text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] rounded px-2 py-1.5">
          {error}
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="text-xs text-[var(--text-ter)] px-2 py-1 rounded hover:bg-[var(--hover-tint)]">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="text-xs bg-[var(--slate)] hover:bg-[var(--slate)] text-white px-3 py-1 rounded font-medium disabled:opacity-50"
        >
          {submitting ? 'Creating…' : 'Create Link'}
        </button>
      </div>
    </form>
  );
}


function InviteLinkCopy({ token }: { token: string }) {
  const link = `${window.location.origin}/invite/space/${token}`;
  return <CopyButton text={link} />;
}

// ── Create space modal ────────────────────────────────────────────────────────

function CreateSpaceModal({
  workspaceId,
  canCreateOrgSpace = false,
  onClose,
  onCreated,
}: {
  workspaceId: string;
  canCreateOrgSpace?: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [spaceType, setSpaceType] = useState<SpaceType>('personal');
  const [allowGuests, setAllowGuests] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = useUserStore(s => s.userId);
  const availableTypes: SpaceType[] = canCreateOrgSpace
    ? ['personal', 'team', 'org']
    : ['personal', 'team'];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setSubmitting(true);
    setError(null);

    const space = await createSpace({
      workspaceId,
      name: name.trim(),
      description: description.trim() || undefined,
      spaceType,
      ownerId: spaceType === 'personal' ? userId : undefined,
      allowGuests,
    });

    setSubmitting(false);
    if (space) {
      onCreated();
    } else {
      setError('Failed to create space. You may not have permission.');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">New Space</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Type picker */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">Space Type</label>
            <div className={`grid gap-2 ${availableTypes.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
              {availableTypes.map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSpaceType(t)}
                  className={`flex flex-col items-center gap-1.5 p-3  border text-sm font-medium transition-colors ${
                    spaceType === t
                      ? 'border-primary-300 bg-primary-50 text-primary-700'
                      : 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                  }`}
                >
                  <SpaceTypeIcon type={t} className="h-5 w-5" />
                  {SPACE_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-2">
              {spaceType === 'personal' && 'Your own private space. You have full control and can invite guests.'}
              {spaceType === 'team' && 'A shared space for a team or cross-functional group.'}
              {spaceType === 'org' && 'Visible to all workspace members. Only one org space per workspace.'}
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={
                spaceType === 'personal' ? 'My Space' :
                spaceType === 'team' ? 'Engineering Team' : 'Acme Corp'
              }
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description <span className="text-[var(--text-ter)] font-normal">(optional)</span></label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this space for?"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          {/* Allow guests */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowGuests}
              onChange={e => setAllowGuests(e.target.checked)}
              className="rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-indigo-100"
            />
            <span className="text-sm text-[var(--text-sec)]">Allow external guests to be invited</span>
          </label>

          {error && (
            <div className="text-sm text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
              {error}
            </div>
          )}

          {/* Footer inside the form so the submit button is a real form submit */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-[var(--rule)] -mx-6 px-6 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors disabled:opacity-50"
            >
              {submitting ? 'Creating…' : 'Create Space'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}

// ── Space policies section ────────────────────────────────────────────────────

interface SpacePolicyRow {
  id: string;
  name: string;
  description?: string;
  rule_type: string;
  enabled: boolean;
  priority: number;
}

function SpacePoliciesSection({ spaceId }: { spaceId: string }) {
  const [policies, setPolicies] = useState<SpacePolicyRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const supabase = getSupabaseClient();
      const { data } = await supabase
        .from('policies')
        .select('id, name, description, rule_type, enabled, priority')
        .eq('scope_kind', 'space')
        .in('scope_id', [spaceId, '*'])
        .order('priority', { ascending: false });
      if (!cancelled) {
        setPolicies((data as SpacePolicyRow[] | null) ?? []);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider">
          Policies ({policies.length})
        </h4>
        <Link
          to={`/prioritization/settings/policies?scope_kind=space&scope_id=${spaceId}`}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"
        >
          <FileText className="h-3.5 w-3.5" />
          Manage policies
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[var(--text-ter)]">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Loading policies…
        </div>
      ) : policies.length === 0 ? (
        <p className="text-xs text-[var(--text-ter)] italic">
          No policies attached. Use the Policies page to add governance rules — e.g. require approval before inviting external guests.
        </p>
      ) : (
        <div className="space-y-1">
          {policies.map(p => (
            <div key={p.id} className="flex items-center gap-2 py-1.5 px-2 bg-[var(--bg-raised)] rounded border border-[var(--rule)]">
              <Shield className={`h-3.5 w-3.5 flex-shrink-0 ${p.enabled ? 'text-primary-600' : 'text-[var(--text-ter)]'}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-[var(--text-pri)] truncate">{p.name}</div>
                {p.description && (
                  <div className="text-xs text-[var(--text-ter)] truncate">{p.description}</div>
                )}
              </div>
              <span className="text-xs text-[var(--text-ter)] capitalize flex-shrink-0">
                {p.rule_type.replace(/_/g, ' ')}
              </span>
              {!p.enabled && (
                <span className="text-xs text-[var(--text-ter)] italic flex-shrink-0">disabled</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
